import { ListView, ListViewColumnType, FilterType, initListView } from '../../UI/view/list-view';
import { allOrdersDS, ordersPermissions } from './orders-datasource';
import { OrderType, isDewarOnlyFilter, ordersListDebounce } from './orders-types';
import { getSourcePlantOptions } from '../../../services/requests/orders/orders-requests';

// All Orders tab: non-deleted orders across all types and states, server-side paged.
// Status is applied in LINQ by the controller; customer/order lookups use global search.
export const allOrdersListView = {
  permissions: ordersPermissions,
  addButtonText: 'Create Order',
  addButtonPath: '/orders/create',
  filterGroup: 'orders',
  secondarySort: 'Id desc',
  ...ordersListDebounce,
  filters: [
    {
      fieldName: 'orderTypeId',
      type: FilterType.multiselect,
      filterValue: { [1]: 1, [2]: 2, [3]: 3 },
      options: () => [1, 2, 3],
      labels: { 1: 'Gas', 2: 'Liquid', 3: 'Dewar' },
    },
    {
      fieldName: 'shippingLocationName',
      type: FilterType.combo,
      label: 'Source Plant',
      searchable: true,
      options: getSourcePlantOptions,
      serverExpression: 'ShippingLocation.LocationName == @val',
      valExpression: (val) => '"' + val + '"',
    },
    {
      fieldName: 'statusText',
      type: FilterType.combo,
      label: 'Status',
      options: () => ['Pending Order', 'Pending Ship', 'Completed', 'Closed', 'Canceled'],
    },
  ],
  columns: [
    // Customer Name is the edit link (leftmost) because an unplaced order has no
    // Order # to click; every order still has a customer + internal id.
    {
      Header: 'Customer Name',
      accessor: 'customerName',
      type: ListViewColumnType.linkEdit,
      linkPath: ({ row }) => `/orders/edit/${row?.original?.id}`,
      excludeGlobalFilter: true,
      serverExpression:
        '(OrderTypeId == 1 ? GasCustomer.CustomerName : (OrderTypeId == 2 ? LiquidCustomer.CustomerName : (OrderTypeId == 3 ? DewarCustomer.AccountName : "")))',
    },
    {
      Header: 'Order #',
      accessor: 'orderNumber',
      excludeGlobalFilter: true,
    },
    { Header: 'Booking ID', id: 'bookingId', excludeGlobalFilter: true, accessor: ({ bookingId }) => (bookingId ? bookingId : '') },
    {
      Header: 'Port Cutoff',
      id: 'portCutoff',
      disableSortBy: true,
      excludeGlobalFilter: true,
      accessor: ({ bookingPortCutoffDateString }) => bookingPortCutoffDateString || '',
    },
    {
      Header: 'Date Requested',
      accessor: 'dateRequested',
      excludeGlobalFilter: true,
      type: ListViewColumnType.date,
    },
    { Header: 'City', accessor: 'city', excludeGlobalFilter: true },
    { Header: 'State', accessor: 'state', excludeGlobalFilter: true },
    { Header: 'Container #', accessor: 'containerId', excludeGlobalFilter: true, show: (_viewOptions, filtersState) => !isDewarOnlyFilter(filtersState) },
    {
      Header: 'Location',
      id: 'shippingCountry',
      excludeGlobalFilter: true,
      serverExpression: 'ShippingLocation.ResidentCountry',
      accessor: ({ shippingLocation }) => shippingLocation?.residentCountry || '',
    },
    {
      Header: 'Source Plant',
      id: 'shippingLocationName',
      excludeGlobalFilter: true,
      serverExpression: 'ShippingLocation.LocationName',
      accessor: ({ shippingLocation }) => shippingLocation?.locationName || '',
      filter: 'equals',
    },
    {
      Header: 'Delivery Type',
      id: 'deliveryType',
      excludeGlobalFilter: true,
      serverExpression: 'DeliveryType.Name',
      accessor: ({ deliveryType }) => deliveryType?.name || '',
    },
    {
      Header: 'PO #/Ord ID',
      id: 'ponumber',
      excludeGlobalFilter: true,
      accessor: ({ ponumber, orderId }) => orderId || ponumber || '',
    },
    {
      Header: 'Status',
      id: 'statusText',
      disableSortBy: true,
      excludeGlobalFilter: true,
      accessor: ({ statusText }) => statusText || '',
    },
    {
      Header: 'MRI/NMR',
      id: 'isTransfill',
      disableSortBy: true,
      excludeGlobalFilter: true,
      accessor: ({ isTransfill }) => isTransfill,
    },
    {
      Header: 'P/U Only',
      id: 'isPickupOnly',
      disableSortBy: true,
      excludeGlobalFilter: true,
      accessor: ({ isPickupOnly }) => (isPickupOnly ? 'Yes' : ''),
    },
    {
      Header: 'Order Type',
      id: 'orderType',
      excludeGlobalFilter: true,
      serverExpression: 'OrderTypeId',
      accessor: ({ orderTypeId }) =>
        orderTypeId === OrderType.Gas
          ? 'Gas'
          : orderTypeId === OrderType.Liquid
          ? 'Liquid'
          : orderTypeId === OrderType.Dewar
          ? 'Dewar'
          : '',
    },
    {
      Header: 'Manifest',
      id: 'manifest',
      disableSortBy: true,
      excludeGlobalFilter: true,
      show: (_viewOptions, filtersState) => isDewarOnlyFilter(filtersState),
      accessor: ({ dewarManifestLine }) => dewarManifestLine || '',
    },
    // Hidden, search-only columns: global search is limited to these id fields
    // (Order #, Order Id, Customer #) — every display column above is excluded.
    // Numeric columns use .ToString() so the backend's string(object(..)) wrapper
    // doesn't blow up casting an int to a string.
    { Header: 'Order # (search)', id: 'orderNumberSearch', accessor: 'orderNumber', serverExpression: 'OrderNumber.ToString()', hidden: true },
    { Header: 'Order Id (search)', id: 'orderIdSearch', accessor: 'orderId', hidden: true },
    { Header: 'Customer # (search)', id: 'customerIdSearch', accessor: 'customerId', serverExpression: 'CustomerId.ToString()', hidden: true },
    // Customer name is globally searchable via a COALESCE (not the ternary the
    // display column uses for sort) so it survives the backend string(object(..))
    // global-search wrapper.
    { Header: 'Customer Name (search)', id: 'customerNameSearch', accessor: 'customerName', serverExpression: 'GasCustomer.CustomerName ?? LiquidCustomer.CustomerName ?? DewarCustomer.AccountName', hidden: true },
    // Hidden, filter-only column backing the client-side Order Type filter.
    { Header: 'Order Type Id', id: 'orderTypeId', accessor: 'orderTypeId', hidden: true, excludeGlobalFilter: true },
  ],
} as ListView;

export const AllOrdersList = initListView(allOrdersListView, allOrdersDS);
